import React, { useEffect, useMemo, useRef, useState } from "react";

/** ───────── Color utils (HSV <-> RGB/HEX) ───────── */
function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }

function hexToRgb(hex) {
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map(c => c + c).join("");
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex({ r, g, b }) {
    const toHex = (v) => v.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}
function rgbToHsv({ r, g, b }) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const d = max - min;
    let h;
    if (d === 0) h = 0;
    else if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = Math.round((h * 60 + 360) % 360);
    const s = max === 0 ? 0 : d / max;
    const v = max;
    return { h, s: Math.round(s * 100), v: Math.round(v * 100) };
}
function hsvToRgb({ h, s, v }) {
    const S = s / 100, V = v / 100;
    const C = V * S;
    const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = V - C;
    let r = 0, g = 0, b = 0;
    if (0 <= h && h < 60) { r = C; g = X; b = 0; }
    else if (60 <= h && h < 120) { r = X; g = C; b = 0; }
    else if (120 <= h && h < 180) { r = 0; g = C; b = X; }
    else if (180 <= h && h < 240) { r = 0; g = X; b = C; }
    else if (240 <= h && h < 300) { r = X; g = 0; b = C; }
    else { r = C; g = 0; b = X; }
    return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}
function hexToHsv(hex) { return rgbToHsv(hexToRgb(hex)); }
function hsvToHex(hsv) { return rgbToHex(hsvToRgb(hsv)); }

/** ───────── MobileColorPicker Modal ───────── */
export default function MobileColorPicker({
    open,
    color = "#000000",
    onChange,
    onClose,
    title = "Color Picker",
}) {


    const svRef = useRef(null);
    const pickingRef = useRef(false);

    // Initialize from prop once, then keep internal HSV stable while open.
    const [hsv, setHSV] = useState(() => {
        try { return hexToHsv(color); } catch { return { h: 0, s: 0, v: 0 }; }
    });
    useEffect(() => {
        if (open) {
            // Sync only when opening the modal, not on every parent color change
            try { setHSV(hexToHsv(color)); } catch { }
        }
    }, [open]); // <-- note: no `color` here on purpose

    // Draw the SV square (for the current hue)
    const drawSV = () => {
        const canvas = svRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        // Use CSS pixels so drawing matches what you see on screen.
        // clientWidth/Height (content box) — not getBoundingClientRect, which
        // includes the border and would mismatch the bitmap sizing below.
        const cssW = canvas.clientWidth;
        const cssH = canvas.clientHeight;

        // Clear in CSS pixels
        ctx.clearRect(0, 0, cssW, cssH);

        // Horizontal saturation gradient (white -> pure hue)
        const satGrad = ctx.createLinearGradient(0, 0, cssW, 0);
        const pureHue = rgbToHex(hsvToRgb({ h: hsv.h, s: 100, v: 100 }));
        satGrad.addColorStop(0, "#FFFFFF");
        satGrad.addColorStop(1, pureHue);
        ctx.fillStyle = satGrad;
        ctx.fillRect(0, 0, cssW, cssH);

        // Vertical value gradient (top transparent -> bottom black)
        const valGrad = ctx.createLinearGradient(0, 0, 0, cssH);
        valGrad.addColorStop(0, "rgba(0,0,0,0)");
        valGrad.addColorStop(1, "rgba(0,0,0,1)");
        ctx.fillStyle = valGrad;
        ctx.fillRect(0, 0, cssW, cssH);

        // Cursor (still in CSS pixels)
        const x = (hsv.s / 100) * cssW;
        const y = cssH - (hsv.v / 100) * cssH;

        const outer = 14;  // make these as big as you like
        const inner = 13;

        ctx.beginPath();
        ctx.arc(x, y, outer, 0, Math.PI * 2);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#000";
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(x, y, inner, 0, Math.PI * 2);
        ctx.strokeStyle = "#fff";
        ctx.stroke();
    };

    // Add a function to size canvas correctly
    const sizeCanvasForDPR = () => {
        const canvas = svRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        // Content-box size: getBoundingClientRect includes the 1px border,
        // and since the canvas has height:auto, writing border-inflated
        // dimensions into the bitmap grows the element a little on every
        // call — a visible creep now that this runs per color change.
        const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
        const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.scale(dpr, dpr); // make drawing commands use CSS pixels
        }
    };

    // Size + draw on open and on every HSV change. Must be a single effect:
    // with separate effects, the on-open rAF/timeout captured drawSV from the
    // pre-sync render and repainted the SV square with the stale color after
    // the correct draw had already happened. Keying on hsv means each change
    // reschedules with a fresh closure and cancels any stale callbacks.
    useEffect(() => {
        if (!open) return;
        const raf = requestAnimationFrame(() => {
            sizeCanvasForDPR();
            drawSV();
        });
        const t = setTimeout(() => {
            sizeCanvasForDPR();
            drawSV();
        }, 0);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t);
        };
    }, [open, hsv.h, hsv.s, hsv.v]);

    // Pick S & V from the canvas
    const updateSVFromEvent = (e) => {
        const canvas = svRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        let x = clamp(clientX - rect.left, 0, rect.width);
        let y = clamp(clientY - rect.top, 0, rect.height);

        const s = Math.round((x / rect.width) * 100);
        const v = Math.round(((rect.height - y) / rect.height) * 100);

        setHSV(prev => ({ ...prev, s, v }));
        onChange && onChange(hsvToHex({ ...hsv, s, v }));
    };

    const handlePointerDown = (e) => { pickingRef.current = true; updateSVFromEvent(e); };
    const handlePointerMove = (e) => { if (pickingRef.current) updateSVFromEvent(e); };
    const handlePointerUp = () => { pickingRef.current = false; };

    // Slider backgrounds
    const hueGradient = `linear-gradient(to right,
    #FF0000, #FFFF00, #00FF00, #00FFFF, #0000FF, #FF00FF, #FF0000)`;

    const satGradient = `linear-gradient(to right,
    ${hsvToHex({ h: hsv.h, s: 0, v: hsv.v })},
    ${hsvToHex({ h: hsv.h, s: 100, v: hsv.v })})`;

    const valGradient = `linear-gradient(to right,
    ${hsvToHex({ h: hsv.h, s: hsv.s, v: 0 })},
    ${hsvToHex({ h: hsv.h, s: hsv.s, v: 100 })})`;

    const selectedHex = hsvToHex(hsv);

    if (!open) return null;

    return (
        <div style={styles.backdrop} onClick={onClose}>
            <div
                className="color-picker-modal"         // ← add class here
                style={styles.modal}
                onClick={(e) => e.stopPropagation()}
            >

                {/* Preview + hex */}
                <div style={styles.previewRow}>
                    <div style={{ ...styles.swatch, background: selectedHex }} />
                    <div style={styles.hex}>{selectedHex}</div>
                </div>

                {/* SV Square */}
                <div style={styles.svWrap}>
                    <canvas
                        ref={svRef}
                        width={360} height={260}
                        style={styles.svCanvas}
                        onMouseDown={handlePointerDown}
                        onMouseMove={handlePointerMove}
                        onMouseUp={handlePointerUp}
                        onMouseLeave={handlePointerUp}
                        onTouchStart={handlePointerDown}
                        onTouchMove={handlePointerMove}
                        onTouchEnd={handlePointerUp}
                    />
                </div>

                {/* Sliders */}
                <div style={styles.sliderBlock}>

                    <input
                        className="modal-interactive"
                        type="range" min="0" max="360" value={hsv.h}
                        onChange={(e) => {
                            const h = Number(e.target.value);
                            setHSV(prev => ({ ...prev, h }));
                            onChange && onChange(hsvToHex({ ...hsv, h }));
                        }}
                        style={{ ...styles.range, background: hueGradient }}
                    />
                </div>

                <div style={styles.sliderBlock}>

                    <input
                        className="modal-interactive"
                        type="range" min="0" max="100" value={hsv.s}
                        onChange={(e) => {
                            const s = Number(e.target.value);
                            setHSV(prev => ({ ...prev, s }));
                            onChange && onChange(hsvToHex({ ...hsv, s }));
                        }}
                        style={{ ...styles.range, background: satGradient }}
                    />
                </div>

                <div style={styles.sliderBlock}>

                    <input
                        className="modal-interactive"
                        type="range" min="0" max="100" value={hsv.v}
                        onChange={(e) => {
                            const v = Number(e.target.value);
                            setHSV(prev => ({ ...prev, v }));
                            onChange && onChange(hsvToHex({ ...hsv, v }));
                        }}
                        style={{ ...styles.range, background: valGradient }}
                    />
                </div>

                <div style={styles.footer}>
                    <button className="modal-interactive" style={styles.done} onClick={onClose}>Done</button>
                </div>
            </div>
        </div>
    );
}

/** ───────── Styles (big touch targets, mobile-friendly) ───────── */
const styles = {
    backdrop: {
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000
    },
    modal: {
        background: "#fff", width: "92vw", maxWidth: 520, borderRadius: 16,
        padding: 16, boxShadow: "0 10px 30px rgba(0,0,0,0.25)"
    },
    previewRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 },
    swatch: { width: 44, height: 44, borderRadius: 8, border: "1px solid #ccc" },
    hex: { fontFamily: "monospace", fontSize: 16 },
    svWrap: { display: "flex", justifyContent: "center", margin: "8px 0 12px" },
    svCanvas: {
        width: "92%", maxWidth: 480, height: "auto",
        touchAction: "none", borderRadius: 8, border: "1px solid #ccc"
    },
    sliderBlock: { marginBottom: 10 },
    label: { display: "block", fontSize: 14, marginBottom: 6 },
    range: {
        WebkitAppearance: "none", width: "100%", height: 22, borderRadius: 12,
        outline: "none", border: "1px solid #ccc"
    },
    footer: { display: "flex", justifyContent: "flex-end", marginTop: 6 },
    done: {
        background: "#111", color: "#fff", border: "none",
        padding: "10px 16px", borderRadius: 10, fontSize: 16
    }
};
